import {
  ApplicationRef,
  Component,
  createComponent,
  EnvironmentInjector,
  Inject,
  inject,
  ViewChild,
} from '@angular/core';
import { SharedModule } from '../../../shared.module';
import { CommonModule } from '@angular/common';
import { BaseComponent } from 'src/app/common/components/base.component';
import { PlaceBusinessStatus, VisitorPlace } from '../../../models/visitor.place';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { RichTextBoxComponent } from 'src/app/common/components/rich.text.box/rich.text.box.component';
import { DashboardService } from 'src/app/modules/dashboard/services/dashboard.service';
import { localStorageKeys } from 'src/app/common/constants/local.storage.keys';
import { TokenInfo } from 'src/app/common/services/auth/auth.service';
import jwt_decode from 'jwt-decode';
import { ButtonTriggerEvent } from 'src/app/common/components/button/button.component';
import { ModalService } from 'src/app/common/services/modal/modal.service';
import {
  EditPlaceImagesModalActions,
  EditPlaceImagesModalComponent,
} from '../../../../place-calendar/components/edit.place.images.modal/edit.place.images.modal.component';
import { FeatureToggle } from '../../../models/feature.toggle';
import {
  EditPlaceCategoryModalActions,
  EditPlaceCategoryModalComponent,
  IEditPlaceCategoryModalData,
} from 'src/app/modules/place-calendar/components/edit.place.category.modal/edit.place.category.modal.component';
import { ErrorAlertService } from 'src/app/common/services/error.alert/error.alert.service';
import { ImageService } from 'src/app/common/services/image/image.service';
import { config } from 'src/app/config/config';
import { VisitorPlaceService } from 'src/app/modules/event-calendar/services/visitor.place.service';
import { EditPlaceCommand } from '../../../models/edit.place.command';
import { AiDescriptionGeneratorComponent } from '../ai.description.generator/ai.description.generator.component';
import { takeUntil } from 'rxjs';

@Component({
  selector: 'ig-edit-place-modal',
  standalone: true,
  imports: [SharedModule, CommonModule, ReactiveFormsModule, AiDescriptionGeneratorComponent],
  templateUrl: './edit.place.modal.component.html',
  styleUrl: './edit.place.modal.component.scss',
})
export class EditPlaceModalComponent extends BaseComponent {
  place: VisitorPlace;

  lockTip = `Currently Unlocked (default/recommended):  Locking will prevent this field from being updated on future automated refreshes.`;

  form: FormGroup;
  _fb = inject(FormBuilder);
  showEditGoogleImagesBtn = false;
  aiGeneratedDescriptionList: string[] = [];
  isGeneratingDescription = false;

  imageLink: string;
  isUploadImage = false;
  selectedImageData: string | ArrayBuffer;
  selectedImageFile: File;

  toggle: FeatureToggle = this.frontendSettings.toggle;

  placeService = inject(VisitorPlaceService);
  modalService = inject(ModalService);
  dashboardService = inject(DashboardService);
  errorAlertService = inject(ErrorAlertService);
  imageService = inject(ImageService);
  environmentInjector = inject(EnvironmentInjector);
  appRef = inject(ApplicationRef);

  @ViewChild('richTextBox', { static: false })
  richTextBox: RichTextBoxComponent;

  constructor(
    public activatedRoute: ActivatedRoute,
    public dialogRef: MatDialogRef<EditPlaceModalComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: VisitorPlace
  ) {
    super({ activatedRoute });
    this.place = data;

    this.form = this._fb.group({
      name: [null, [Validators.required]],
      address: [null, []],
      description: [null, []],
      website: [null, []],
      phone: [null, []],
      priceRange: [null, []],
      business_status: [null, []],
      categoryText: [null, []],

      ownerEmail: [null, [Validators.email]],
      ownerPhone: [null, []],
      _360VideoKey: [null, []],
      youtubeLink: [false, []],
      facebookLink: [false, []],
      hostLink: [null, []],
      isHideTicketUrl: [false, []],

      audio_link: [null, []],
      audio_description: [null, []],
    });

    this.form.patchValue(this.place);
    if (this.place.address) {
      this.form.get('address').setValue(this.place.address);
    }
    if (this.place.audio && this.place.audio.isActive) {
      this.form.get('audio_link').setValue(this.place.audio.link);
      this.form.get('audio_description').setValue(this.place.audio.description);
    }

    // close edit place modal, when generating AI descriptions. Otherwise the dropdowns on the AI generator modal will be covered by the modal itself.
    DashboardService.info$.pipe(takeUntil(this.isDestroyed)).subscribe((info) => {
      if (info.type === 'BULK_GENERATE_DESCRIPTIONS') {
        this.dialogRef.close();
      }
    });
  }

  onAIGeneratedDescriptionSelected(desc: string) {
    if (desc) {
      this.form.get('description').setValue(desc);
      this.richTextBox.setEditorContent(desc);
    }
  }

  async saveChanges(event: ButtonTriggerEvent) {
    let command = <EditPlaceCommand>this.form.value;

    if (command.youtubeLink && command.youtubeLink.indexOf('/watch?v=') < 0) {
      this.errorAlertService.showErrorAlert('Please specify a correct Youtube video link.');
      event.immediateComplete();
      return;
    }

    if (!command.categoryText) {
      event.immediateComplete();
      this.errorAlertService.showErrorAlert('Please specify a category.');
      return;
    }

    if (this.place.audio) {
      if (this.place.audio.isActive) {
        if (!command.audio_link) {
          event.immediateComplete();
          this.errorAlertService.showErrorAlert('Please add audio tour link.');
          return;
        } else {
          if (!this.isAudioUrl(command.audio_link)) {
            event.immediateComplete();
            this.errorAlertService.showErrorAlert('The audio tour link is not an audio file link.');
            return;
          }
        }
        command.audio = {
          isActive: this.place.audio.isActive,
          link: command.audio_link,
          description: command.audio_description,
        };
      }

      if (!this.place.audio.isActive && command.audio_link) {
        command.audio_link = null;
        command.audio_description = null;
      }
    }

    // await this.processImage();
    this.processLinks(command);

    if (command.business_status === PlaceBusinessStatus.CLOSED_PERMANENTLY) {
      command.isApproved = false;
    } else if (this.place.business_status === PlaceBusinessStatus.CLOSED_TEMPORARILY) {
      if (this.frontendSettings?.customSettings?.isKeepTempClosedPlaceInApproved === false) {
        command.isApproved = false;
      }
    }

    command.placeId = this.place._id;
    command.title = this.place.name;
    command.cover = this.place.cover;

    command.allowedGooglePhotos = this.place.allowedGooglePhotos;
    command.blockedGooglePhotos = this.place.blockedGooglePhotos;
    command.ownedPhotos = this.place.ownedPhotos;

    command.address = this.processAddress();

    if (typeof command.address === 'string') {
      command.address = {
        lat: this.place.lat,
        lng: this.place.lng,
        address: command.address,
        address_components: this.place['address_components'],
      };
    }
    command.lock = this.place.lock;

    Object.assign(this.place, {
      ...command,
      ...(this.place.cover && { cover: this.place.cover }),
      address: command.address.address || this.place.address.address,
      address_components:
        command.address.address_components || this.place.address.addressComponents,
    });

    this.dashboardService.editPlace(this.place.hubName, command).subscribe({
      next: (image) => {
        if (image) {
          if (!this.place.cover) {
            this.place.cover = { source: '', isDefault: false };
          }
          this.place.cover.source = image;
        }

        if (!window.location.href.includes('localhost')) {
          const authToken = localStorage.getItem(localStorageKeys.authToken);
          let tokenInfo = jwt_decode<TokenInfo>(authToken);
          try {
            let trackName = 'editPlace - ' + this.place.name;
            window['pendo'].track(trackName, {
              accountId: tokenInfo.userName,
              timestamp: Date.now(),
              hubName: this.place.hubName,
            });
          } catch (error) {
            console.error(error);
          }
        }
        this.close(this.place);
        this.showSuccess('Place updated successfully.');
      },
      error: (err) => {
        event.complete();
        console.log(err);
      },
    });
  }

  lockInfo(field) {
    this.place.lock[field] = !this.place.lock[field];
  }

  onDescriptionChange(content: string) {
    this.form.get('description').setValue(content);
  }

  onGenerateDescription() {
    if (this.isGeneratingDescription) return;
    this.isGeneratingDescription = true;

    this.dashboardService
      .aiAssistant(this.place.hubName, {
        query: this.getPlaceNameAndAddress(),
        model: 'chatgpt',
        feature: 'place-description',
      })
      .subscribe((data: { success: boolean; model: string; data: string }) => {
        if (data.success) {
          try {
            const json = JSON.parse(data.data);
            this.aiGeneratedDescriptionList = [json.version1, json.version2, json.version3];
            console.log(this.aiGeneratedDescriptionList);
            const modal = document.querySelector('.ig-edit-place-modal');
            modal &&
              (modal.className = modal.className.replace('width-modal-1', 'width-modal-70p'));

            this.trackPlaceAIContentAssistant(this.place);
          } catch (err) {
            this.isGeneratingDescription = false;
            console.error(err);
          }
        }
      });
  }

  toggleAudioTour() {
    if (!this.place.audio) {
      this.place.audio = {
        isActive: false,
        link: '',
        description: '',
      };
    }
    this.place.audio.isActive = !this.place.audio.isActive;
  }

  showEditCategoryModal() {
    let actions = <EditPlaceCategoryModalActions>{
      confirm: (event: ButtonTriggerEvent, category) => {
        this.form.get('categoryText').setValue(category.categoryText);
        this.place.categoryList = category.categoryList;
      },
    };

    this.modalService.show({
      component: EditPlaceCategoryModalComponent,
      panelClass: 'ig-modal-w-70p',
      data: {
        actions,
        place: {
          categoryList: this.form.get('categoryText').value
            ? this.form
                .get('categoryText')
                .value.split(',')
                .map((x) => this.toCode(x.trim()))
            : [],
        } as VisitorPlace,
      } as IEditPlaceCategoryModalData,
    });
  }

  toCode(category: string) {
    return this.placeService.toCategoryCode(category);
  }

  showEditImagesModal() {
    // if (this.place.isManuallyCreated) {
    //   let actions = <EditManuallyPlaceImagesModalActions>{
    //     confirm: (event: ButtonTriggerEvent, place, coverImg) => {
    //       this.place = place;
    //       this.imageLink = coverImg;

    //       if (this.place.cover) {
    //         this.place.cover.source = coverImg;
    //       } else {
    //         this.place.cover = { source: coverImg, isDefault: true };
    //       }
    //     },
    //   };
    //   this.modalService.show({
    //     component: EditManuallyPlaceImagesModalComponent,
    //     data: {
    //       place: this.place,
    //       actions,
    //     },
    //     panelClass: ['ig-edit-modal', '!w-[1000px]'],
    //   });
    // } else {
    let actions = <EditPlaceImagesModalActions>{
      confirm: (event: ButtonTriggerEvent, place, coverImg) => {
        this.place = place;
        this.imageLink = coverImg;
        if (this.place.cover) {
          this.place.cover.source = coverImg;
        } else {
          this.place.cover = { source: coverImg, isDefault: true };
        }
      },
    };
    this.modalService.show({
      component: EditPlaceImagesModalComponent,
      data: {
        place: this.place,
        actions,
      },
      panelClass: ['ig-edit-modal', '!w-[1100px]'],
    });
    // }
  }

  close(place: VisitorPlace) {
    this.dialogRef.close(place);
  }

  private processAddress() {
    const address = this.form.get('address').value;

    if (!address) {
      return address;
    }

    // doesn't change the address
    if (typeof address === 'string' && this.place.address.toString() === address) {
      return this.place.address;
    }

    if (typeof address === 'object') {
      const processedAddress = { ...address };

      if (processedAddress.addressComponents) {
        processedAddress.address_components = processedAddress.addressComponents;
        delete processedAddress.addressComponents;
      }

      return processedAddress;
    }

    return address;
  }

  private getPlaceNameAndAddress() {
    return `${this.place.name}, ${this.place.address ? this.place.address.address : ''}`;
  }

  private trackPlaceAIContentAssistant(place: VisitorPlace) {
    if (!window.location.href.includes('localhost')) {
      const authToken = localStorage.getItem(localStorageKeys.authToken);
      let tokenInfo = jwt_decode<TokenInfo>(authToken);
      try {
        let trackName = 'click - Place - AIContentAssistant - ' + place.name;
        window['pendo'].track(trackName, {
          accountId: tokenInfo.userName,
          timestamp: Date.now(),
          hubName: place.hubName,
        });
      } catch (error) {
        console.error(error);
      }
    }
  }

  private isAudioUrl(url: string): boolean {
    const audioExtensions = ['.mp3', '.wav', '.ogg', '.aac', '.flac', '.m4a', '.mp4'];
    return audioExtensions.some((ext) => url.toLowerCase().endsWith(ext));
  }

  private async processImage() {
    if (this.isUploadImage) {
      if (this.selectedImageFile) {
        let image = await this.imageService.compressImageFileToBase64(
          this.selectedImageFile,
          config.compressImgWidth
        );
        // max limit size is 1MB
        if (image.length > 1000000) {
          image = await this.imageService.compressImageFileToBase64(
            this.selectedImageFile,
            config.compressImgWidth - 200
          );
        }
        // save to AWS album "places/iti-user-upload" to distinguish user load image from place pulled from google image
        // so that if user edit (allow, block) Google images, it will know if need to update cover image accordingly
        this.imageService
          .uploadBase64ImageToAWS(image, 960, 'places/iti-user-upload/')
          .subscribe((res) => {
            if (res.success) {
              this.place.cover = { source: '', isDefault: false };
              this.place.cover.source = this.imageLink = this.place.imageLink = res.url;
            }
          });
      } else {
        this.place.imageLink =
          this.imageLink || (this.place.cover && this.place.cover.source) || null;
      }
    } else {
      this.place.imageLink =
        this.imageLink || (this.place.cover && this.place.cover.source) || null;
      if (this.place.imageLink && !this.place.imageLink.includes('amazonaws')) {
        this.imageService
          .uploadBase64ImageToAWS(this.place.imageLink, 960, 'places/iti-user-upload/')
          .subscribe((res) => {
            if (res.success) {
              this.place.cover = { source: '', isDefault: false };
              this.place.cover.source = this.imageLink = this.place.imageLink = res.url;
            }
          });
      }
    }
    this.place.imageFile = null;
  }

  private processLinks(cmd: EditPlaceCommand) {
    cmd.hostLink = this.addLinkPrefix(cmd.hostLink);
    cmd.youtubeLink = this.addLinkPrefix(cmd.youtubeLink);
    cmd.facebookLink = this.addLinkPrefix(cmd.facebookLink);
    cmd.website = this.addLinkPrefix(cmd.website);

    if (this.place.audio && this.place.audio.isActive) {
      cmd.audio_link = this.addLinkPrefix(cmd.audio_link);
    }
  }

  private addLinkPrefix(link: string | undefined): string {
    const linkWithPrefix = link ? (link.startsWith('http') ? link : `https://${link}`) : '';
    return linkWithPrefix;
  }
}
